import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import SafeImage from "../components/site/SafeImage";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";

const Blog = () => {
  usePageMeta({
    title: "Blog & Insights | Dr. Shantanu Kumar Gupta",
    path: "/blog",
    description: "Patient-friendly articles on cataract surgery, retina care, premium IOLs and diabetic eye health from a Varanasi ophthalmologist.",
  });
  usePageJsonLd(graph([breadcrumb([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }])]));
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("All");

  useEffect(() => { api.get("/blog").then((r) => setItems(r.data)); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const filtered = useMemo(() => items.filter((it) => {
    const catOk = active === "All" || it.category === active;
    const q = query.trim().toLowerCase();
    const qOk = !q || it.title.toLowerCase().includes(q) || it.excerpt.toLowerCase().includes(q) || (it.tags || []).join(" ").toLowerCase().includes(q);
    return catOk && qOk;
  }), [items, active, query]);

  const featured = items.find((i) => i.featured);

  return (
    <div data-testid="blog-page">
      <section className="pt-20 pb-10 container-page">
        <div className="overline mb-4">Blog & Insights</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Notes from the clinic — deep, honest and readable.
        </h1>
      </section>

      {featured && (
        <section className="container-page">
          <Link to={`/blog/${featured.slug}`} className="grid md:grid-cols-2 gap-10 items-center card-elegant p-6 md:p-10" data-testid="featured-post">
            <SafeImage src={featured.cover_image} alt={featured.title} label={featured.title} className="w-full aspect-[4/3] rounded-xl" />
            <div>
              <div className="overline">Featured · {featured.category}</div>
              <h2 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight">{featured.title}</h2>
              <p className="mt-4 prose-elegant line-clamp-3">{featured.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                Read article <ArrowUpRight size={14} />
              </span>
            </div>
          </Link>
        </section>
      )}

      <section className="py-10 border-y border-border mt-16 bg-[hsl(var(--secondary))]/40">
        <div className="container-page flex flex-col md:flex-row md:items-center gap-6">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search blog…" className="pl-9" data-testid="blog-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} onClick={() => setActive(c)} className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.16em] border transition ${
                active === c ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-border text-muted-foreground hover:text-[hsl(var(--primary))]"
              }`} data-testid={`blog-cat-${c.replace(/\s/g, "-").toLowerCase()}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 container-page grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="blog-list">
        {filtered.map((p) => (
          <Link to={`/blog/${p.slug}`} key={p.slug} className="group block" data-testid={`blog-card-${p.slug}`}>
            <div className="overflow-hidden rounded-xl aspect-[4/3] border border-border">
              <SafeImage src={p.cover_image} alt={p.title} label={p.title} className="w-full h-full" />
            </div>
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] flex items-center gap-2 flex-wrap">
                <span>{p.category} · {p.reading_time} min</span>
                {p.has_hindi && (
                  <span data-testid={`blog-card-hindi-badge-${p.slug}`} className="px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] normal-case tracking-normal text-[10px] font-medium border border-[hsl(var(--primary))]/20">
                    हिन्दी में उपलब्ध
                  </span>
                )}
              </div>
              <h3 className="font-serif text-2xl mt-2 group-hover:text-[hsl(var(--primary))] transition-colors leading-snug">{p.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
            </div>
          </Link>
        ))}
        {filtered.length === 0 && <div className="col-span-full py-16 text-center text-muted-foreground">No posts found.</div>}
      </section>
    </div>
  );
};

export default Blog;
